import { useSearchParams, Link } from "react-router-dom";
import { Mail, MapPin, Clock, Phone, Facebook, Instagram, CalendarClock, ArrowUpRight } from "lucide-react";
import { Wordmark, GoldDivider, Eyebrow, EditorialHeading } from "../../components/brand";
import SectionHeader from "../../components/shared/SectionHeader";
import LeadForm from "../../components/shared/LeadForm";
import { BUSINESS_INFO } from "../../content/site";

export default function Contact() {
  const [params] = useSearchParams();
  const type = params.get("type"); // consultation | ""

  return (
    <div data-testid="page-contact">
      <section className="bg-altura-ivory py-20">
        <div className="container-editorial">
          <div className="max-w-3xl">
            <Eyebrow>{type === "consultation" ? "Schedule Consultation" : "Investor Inquiry"}</Eyebrow>
            <EditorialHeading size="xl" className="mt-4">
              {type === "consultation" ? "Begin the conversation." : "Tell us about your investment objectives."}
            </EditorialHeading>
            <GoldDivider className="mt-6" />
            <p className="mt-6 text-altura-charcoal/70 max-w-2xl leading-relaxed">
              An Altura Global advisor will follow up personally to understand your objectives, timeline, and market preferences.
            </p>
          </div>
        </div>
      </section>

      {/* Internal CTAs */}
      <section className="bg-altura-ivory">
        <div className="container-editorial grid md:grid-cols-2 gap-4 pb-4">
          <Link to="/consultation"
             className="group flex flex-col justify-between rounded-lg border border-altura-espresso/15 bg-white p-6 transition-all hover:border-altura-gold hover:shadow-[0_20px_60px_-15px_rgba(58,31,20,0.15)]"
             data-testid="cta-advisory-internal">
            <div>
              <div className="flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-altura-gold-dark" />
                <span className="eyebrow text-altura-gold-dark">Private 30-Minute Session</span>
              </div>
              <div className="mt-3 font-editorial text-2xl text-altura-espresso leading-tight">International Property Investment Advisory</div>
              <p className="mt-3 text-sm text-altura-charcoal/70 leading-relaxed">
                Looking to invest in international real estate but need expert guidance? Altura Global RE will provide personalized advice tailored to your goals — whether you're exploring your first investment or expanding your portfolio globally.
              </p>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-sm text-altura-espresso group-hover:text-altura-gold transition">
              Book a consultation <ArrowUpRight className="h-3.5 w-3.5" />
            </div>
          </Link>

          <Link to="/global-link"
             className="group flex flex-col justify-between rounded-lg border border-altura-gold/40 bg-altura-gold/5 p-6 transition-all hover:bg-altura-gold/10 hover:shadow-[0_20px_60px_-15px_rgba(201,164,90,0.35)]"
             data-testid="cta-global-link-internal">
            <div>
              <span className="eyebrow text-altura-gold-dark">Referral Partner Program</span>
              <div className="mt-3 font-editorial text-2xl text-altura-espresso leading-tight">Join The Global Link</div>
              <p className="mt-3 text-sm text-altura-charcoal/75 leading-relaxed">
                Turn your network into income. Earn a flat <strong className="text-altura-espresso">{BUSINESS_INFO.global_link_commission}</strong> for every successful referral to Altura Global's international real-estate opportunities.
              </p>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-sm text-altura-espresso group-hover:text-altura-gold-dark transition">
              Explore The Global Link <ArrowUpRight className="h-3.5 w-3.5" />
            </div>
          </Link>
        </div>
      </section>

      <section className="bg-altura-ivory pb-24">
        <div className="container-editorial grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-8">
            <div className="rounded-lg border border-altura-espresso/15 bg-white p-8">
              <LeadForm variant={type === "consultation" ? "consultation" : "inquiry"} sourcePage="/contact" testIdPrefix="contact" />
            </div>
          </div>
          <aside className="lg:col-span-4 space-y-8">
            <div data-testid="contact-details">
              <Eyebrow>Global / International Inquiry</Eyebrow>
              <div className="mt-4 space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <Mail className="h-4 w-4 text-altura-gold-dark mt-0.5" />
                  <div>
                    <a href={`mailto:${BUSINESS_INFO.email_primary}`} className="font-editorial text-altura-espresso text-lg hover:text-altura-gold-dark">{BUSINESS_INFO.email_primary}</a>
                    <div className="text-altura-charcoal/60 text-xs mt-0.5">General & investor relations</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-4 w-4 text-altura-gold-dark mt-0.5" />
                  <div>
                    <a href={`tel:${BUSINESS_INFO.phone_global.replace(/[^\d+]/g, "")}`} className="font-editorial text-altura-espresso text-lg hover:text-altura-gold-dark">{BUSINESS_INFO.phone_global}</a>
                    <div className="text-altura-charcoal/60 text-xs mt-0.5">Global · WhatsApp available</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-4 w-4 text-altura-gold-dark mt-0.5" />
                  <div>
                    <a href={`tel:${BUSINESS_INFO.phone_usa.replace(/[^\d+]/g, "")}`} className="font-editorial text-altura-espresso text-lg hover:text-altura-gold-dark">{BUSINESS_INFO.phone_usa}</a>
                    <div className="text-altura-charcoal/60 text-xs mt-0.5">USA / Domestic Inquiry</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-altura-gold-dark mt-0.5" />
                  <div>
                    <div className="font-editorial text-altura-espresso text-lg leading-tight">USA Mailing Address</div>
                    <div className="mt-1 text-altura-charcoal/75 text-sm">9951 Atlantic Blvd. Ste 443<br />Jacksonville, FL 32225</div>
                    <div className="text-altura-charcoal/55 text-xs mt-1.5">Serving clients globally with Ghana as our flagship market.</div>
                  </div>
                </div>
              </div>
            </div>

            <div data-testid="contact-hours">
              <Eyebrow>Business Hours</Eyebrow>
              <div className="mt-4 space-y-2 text-sm">
                {BUSINESS_INFO.hours.map((h) => (
                  <div key={h.label} className="flex justify-between gap-4 border-b border-altura-espresso/10 pb-2">
                    <span className="text-altura-charcoal/75">{h.label}</span>
                    <span className="font-editorial text-altura-espresso">{h.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="contact-socials">
              <Eyebrow>Follow Us</Eyebrow>
              <div className="mt-4 flex items-center gap-2">
                <a href={BUSINESS_INFO.socials.facebook} target="_blank" rel="noreferrer"
                   className="group inline-flex items-center gap-2 rounded-full border border-altura-espresso/15 px-4 py-2 text-xs hover:border-altura-gold hover:text-altura-gold-dark transition"
                   data-testid="social-facebook">
                  <Facebook className="h-3.5 w-3.5" /> Facebook
                </a>
                <a href={BUSINESS_INFO.socials.instagram} target="_blank" rel="noreferrer"
                   className="group inline-flex items-center gap-2 rounded-full border border-altura-espresso/15 px-4 py-2 text-xs hover:border-altura-gold hover:text-altura-gold-dark transition"
                   data-testid="social-instagram">
                  <Instagram className="h-3.5 w-3.5" /> Instagram
                </a>
              </div>
            </div>

            <div className="rounded-lg border border-altura-gold/30 bg-altura-gold/5 p-6">
              <Eyebrow className="text-altura-gold-dark">What happens next</Eyebrow>
              <ol className="mt-4 space-y-2 text-sm text-altura-charcoal/75 list-decimal list-inside">
                <li>An advisor reviews your inquiry within 1 business day.</li>
                <li>We schedule a 30-minute conversation to understand your objectives.</li>
                <li>We build a restrained shortlist of matching opportunities.</li>
                <li>Tours, diligence, and closing coordinated through licensed local partners.</li>
              </ol>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
